import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Grid, Typography, Fab, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ProductsCatalogIcon from "@material-ui/icons/ViewList";
import DownloadIcon from "@material-ui/icons/GetApp";

import translate from "../i18n/Translator";
import Surface from "../components/Surface";
import { UploadProductCatalogRequest, TenantSettings } from "../model/TenantSettings";
import UploaderInput from "../components/UploaderInput";
import { InputRef, isValid } from "../components/ValidatedInput";
import { get, uploadProductCatalog } from "../api/TenantSettingsAPI";
import { AppContext } from "../context/AppContext";
import { ErrorSnackbar, SuccessSnackbar } from "../components/Snackbars";
import Progress from "../components/Progress";
import { formatDate } from "../components/DateFormat";

export default function TenantIssuerProductCatalogUploader() {
    const context = useContext(AppContext);
    const theme = useTheme();
    const isXs = useMediaQuery(() => theme.breakpoints.down("xs"));

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<TenantSettings>();
    const [request, setRequest] = useState<UploadProductCatalogRequest>({ url: "" } as UploadProductCatalogRequest);
    const [submitting, setSubmitting] = useState(false);
    const [validations, setValidations] = useState({} as any);
    const [success, setSuccess] = useState<string>();
    const [error, setError] = useState<string>();

    const hasChanged = (name: string, value: string, inputRef: InputRef) => {
        setRequest({ ...request, [name]: value });
        validations[name] = inputRef;
        setValidations(validations);
    };

    useEffect(() => {
        get(context.session!.tenant!.id).then(response => {
            setData(response);
            setStatus("loaded");
        }).catch(error => {
            setStatus(error.message);
        });
        // eslint-disable-next-line
    }, []);

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isValid(validations)) return;

        setSubmitting(true);
        uploadProductCatalog(context.session!.tenant!.id, request).then(response => {
            setData(response);
            setSuccess(translate("tenants_issuers.products_catalog.updated") as string);
            setRequest({ url: "" } as UploadProductCatalogRequest);
        }).catch(error => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const getLastUpdate = (): string | undefined => {
        if (data && data.product_catalog && data.product_catalog.last_update) {
            const value = formatDate(data.product_catalog.last_update, "DD MMM, YYYY");
            return translate("tenants_issuers.products_catalog.last_update", { "date": value }) as string;
        }
        return undefined;
    };

    const onCloseSnackbars = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    if (status === "loading" || !data) {
        return <Progress />;
    }

    if (status !== "loaded") {
        return (
            <Typography variant="body1" component="h5" color="error" align="center">
                {status}
            </Typography>
        );
    }

    return (
        <Grid item xs={12}>
            <Grid container justify="center" alignItems="center">
                <Grid item xs={12} md={10} lg={7} xl={5}>
                    <Surface title={translate("tenants_issuers.products_catalog.title")} subtitle={getLastUpdate()} icon={<ProductsCatalogIcon />} className="FormSurface" titleActions={data && data.product_catalog && data.product_catalog.url ? (
                        <Fab color="primary" size="small" title={translate("buttons.download") as string} component="a" href={data.product_catalog.url} target="_blank" rel="noopener noreferrer">
                            <DownloadIcon />
                        </Fab>
                    ) : undefined}>
                        <form autoComplete="off" noValidate onSubmit={onSubmit} >
                            <Grid container>
                                <Grid item xs={12}>
                                    <UploaderInput id="url" name="url"
                                        value={request.url} label={translate("tenants_issuers.products_catalog.file") as string}
                                        required disabled={submitting}
                                        margin="dense" acceptExtension=".xlsx"
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box pt={2}>
                                        <Grid container justify="flex-start" spacing={1} direction="row-reverse">
                                            <Grid item xs sm="auto">
                                                <Button type="submit" variant="contained" color="primary" size="large" disabled={submitting} fullWidth={isXs}>
                                                    {translate("buttons.update")}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    </Surface>
                </Grid>
            </Grid>
            <SuccessSnackbar message={success} onClose={onCloseSnackbars} />
            <ErrorSnackbar message={error} onClose={onCloseSnackbars} />
        </Grid>
    )
}